// ─────────────────────────────────────────────────────────────
// STApi — fetch wrapper for api.sundaytriggers.com
// All category/type data is stored in Cloudflare KV or D1 as needed.
// For now categories and types are stored in localStorage so they
// persist locally and travel with the project via meta.json.
// ─────────────────────────────────────────────────────────────

import {STCategory, STSong, STType} from "./STTypes"
import {ClerkAuth} from "../auth/ClerkAuth"

const BASE_URL = "https://api.sundaytriggers.com"

// ── Songs ─────────────────────────────────────────────────────

export namespace STApi {
    // Read. Some library read routes now require auth (the app uses a device
    // pass; the DAW uses its Clerk token). The DAW is always authenticated when
    // booted, so attach the token best-effort; ungated routes ignore it.
    async function req<T>(path: string, options?: RequestInit): Promise<T> {
        let authHeader: Record<string, string> = {}
        try {
            const token = await ClerkAuth.getToken()
            if (token) authHeader = {"Authorization": `Bearer ${token}`}
        } catch { /* not signed in yet — proceed without */ }
        const res = await fetch(`${BASE_URL}${path}`, {
            headers: {"Content-Type": "application/json", ...authHeader},
            ...options,
        })
        if (!res.ok) {
            const text = await res.text().catch(() => res.statusText)
            throw new Error(`ST API ${res.status}: ${text}`)
        }
        return res.json() as Promise<T>
    }

    // Authenticated write — Clerk JWT required
    async function reqWrite<T>(path: string, options?: RequestInit): Promise<T> {
        const token = await ClerkAuth.getToken()
        const res = await fetch(`${BASE_URL}${path}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            ...options,
        })
        if (!res.ok) {
            const text = await res.text().catch(() => res.statusText)
            throw new Error(`ST API ${res.status}: ${text}`)
        }
        return res.json() as Promise<T>
    }

    export const getSongs = async (params?: {status?: string}): Promise<STSong[]> => {
        const qs = params?.status ? `?status=${params.status}` : ""
        // /sync now requires auth; the DAW is always Clerk-authenticated when
        // booted, so read it as the admin (manifests are omitted for the DAW).
        const data = await reqWrite<{songs: STSong[]}>(`/library/sync${qs}`)
        return data.songs ?? []
    }

    // ── Seed localStorage from D1 on startup ─────────────────
    // Fetches canonical types and categories from the server and
    // merges them into localStorage so UUIDs stay in sync across
    // browsers and sessions.

    export const seedFromD1 = async (): Promise<void> => {
        try {
            const [remoteTypes, remoteCategories, remoteLBCategories] = await Promise.all([
                reqWrite<STType[]>("/types"),
                reqWrite<STCategory[]>("/categories"),
                reqWrite<STCategory[]>("/loop-builder-categories"),
            ])

            // Merge remote into local: remote is authoritative for id+name pairs.
            // Any local-only entries (created this session but not yet pushed) are kept.
            const localTypes = getTypes()
            const remoteTypeIds = new Set(remoteTypes.map(type => type.id))
            const localOnlyTypes = localTypes.filter(type => !remoteTypeIds.has(type.id))
            saveTypes([...remoteTypes, ...localOnlyTypes])

            const localCats = getCategories()
            const remoteCatIds = new Set(remoteCategories.map(cat => cat.id))
            const localCatsOnly = localCats.filter(cat => !remoteCatIds.has(cat.id))
            saveCategories([...remoteCategories, ...localCatsOnly])

            const localLBCats = getLoopBuilderCategories()
            const remoteLBCatIds = new Set(remoteLBCategories.map(cat => cat.id))
            const localLBCatsOnly = localLBCats.filter(cat => !remoteLBCatIds.has(cat.id))
            saveLoopBuilderCategories([...remoteLBCategories, ...localLBCatsOnly])
        } catch (e) {
            console.warn("[STApi] seedFromD1 failed — continuing with localStorage:", e)
        }
    }

    export const createSong = (body: Partial<STSong>): Promise<STSong> =>
        reqWrite<STSong>("/library/songs", {method: "POST", body: JSON.stringify(body)})

    export const updateSong = (id: string, body: Partial<STSong>): Promise<STSong> =>
        reqWrite<STSong>(`/library/songs/${id}`, {method: "PUT", body: JSON.stringify(body)})

    // ── Loop Builders ─────────────────────────────────────────

    export const getLoopBuilders = (params?: {status?: string}): Promise<Record<string, unknown>[]> => {
        const qs = params?.status ? `?status=${params.status}` : ""
        return req<Record<string, unknown>[]>(`/library/loop-builders${qs}`)
    }

    export const getLoopBuilder = (id: string): Promise<Record<string, unknown>> =>
        req<Record<string, unknown>>(`/library/loop-builders/${id}`)

    export const createLoopBuilder = (body: Record<string, unknown>): Promise<{id: string}> =>
        reqWrite<{id: string}>("/library/loop-builders", {method: "POST", body: JSON.stringify(body)})

    export const updateLoopBuilder = (id: string, body: Record<string, unknown>): Promise<{id: string}> =>
        reqWrite<{id: string}>(`/library/loop-builders/${id}`, {method: "PUT", body: JSON.stringify(body)})

    // ── Categories (localStorage) ─────────────────────────────

    const CAT_KEY = "st_categories"
    const TYPE_KEY = "st_types"

    export const getCategories = (): STCategory[] => {
        try {
            return JSON.parse(localStorage.getItem(CAT_KEY) ?? "[]")
        } catch {
            return []
        }
    }

    export const saveCategories = (cats: STCategory[]): void => {
        localStorage.setItem(CAT_KEY, JSON.stringify(cats))
    }

    export const createCategory = (name: string, color: string): STCategory => {
        const cat: STCategory = {id: crypto.randomUUID(), name, color}
        saveCategories([...getCategories(), cat])
        return cat
    }

    export const updateCategory = (id: string, patch: Partial<STCategory>): void => {
        saveCategories(getCategories().map(c => c.id === id ? {...c, ...patch} : c))
    }

    export const deleteCategory = async (id: string): Promise<void> => {
        saveCategories(getCategories().filter(c => c.id !== id))
        try { await reqWrite<{deleted: string}>(`/categories/${id}`, {method: "DELETE"}) }
        catch (e) { console.warn("[STApi] deleteCategory API call failed:", e) }
    }

    // ── Loop Builder Categories (localStorage) ────────────────

    const LB_CAT_KEY = "lb_categories"

    export const getLoopBuilderCategories = (): STCategory[] => {
        try {
            return JSON.parse(localStorage.getItem(LB_CAT_KEY) ?? "[]")
        } catch {
            return []
        }
    }

    export const saveLoopBuilderCategories = (cats: STCategory[]): void => {
        localStorage.setItem(LB_CAT_KEY, JSON.stringify(cats))
    }

    export const createLoopBuilderCategory = (name: string, color: string): STCategory => {
        const cat: STCategory = {id: crypto.randomUUID(), name, color}
        saveLoopBuilderCategories([...getLoopBuilderCategories(), cat])
        return cat
    }

    export const updateLoopBuilderCategory = (id: string, patch: Partial<STCategory>): void => {
        saveLoopBuilderCategories(getLoopBuilderCategories().map(c => c.id === id ? {...c, ...patch} : c))
    }

    export const deleteLoopBuilderCategory = async (id: string): Promise<void> => {
        saveLoopBuilderCategories(getLoopBuilderCategories().filter(c => c.id !== id))
        try { await reqWrite<{deleted: string}>(`/loop-builder-categories/${id}`, {method: "DELETE"}) }
        catch (e) { console.warn("[STApi] deleteLoopBuilderCategory API call failed:", e) }
    }

    export const upsertLoopBuilderCategory = (cat: STCategory): Promise<STCategory> =>
        reqWrite<STCategory>(`/loop-builder-categories/${cat.id}`, {method: "PUT", body: JSON.stringify(cat)})

    export const getLoopBuilderUsageCounts = (): Promise<{categories: Record<string, number>}> =>
        req<{categories: Record<string, number>}>("/library/loop-builder-usage-counts")

    // ── Factory Presets ───────────────────────────────────────

    export const getFactoryPresets = (): Promise<Record<string, unknown>[]> =>
        req<Record<string, unknown>[]>("/library/factory-presets")

    export const getFactoryPreset = (id: string): Promise<Record<string, unknown>> =>
        req<Record<string, unknown>>(`/library/factory-presets/${id}`)

    export const createFactoryPreset = (body: Record<string, unknown>): Promise<{id: string}> =>
        reqWrite<{id: string}>("/library/factory-presets", {method: "POST", body: JSON.stringify(body)})

    export const updateFactoryPreset = (id: string, body: Record<string, unknown>): Promise<{id: string}> =>
        reqWrite<{id: string}>(`/library/factory-presets/${id}`, {method: "PUT", body: JSON.stringify(body)})

    // ── Types (localStorage) ──────────────────────────────────

    export const getTypes = (): STType[] => {
        try {
            return JSON.parse(localStorage.getItem(TYPE_KEY) ?? "[]")
        } catch {
            return []
        }
    }

    export const saveTypes = (types: STType[]): void => {
        localStorage.setItem(TYPE_KEY, JSON.stringify(types))
    }

    export const createType = (name: string, color: string, showInFilter = false, iconName?: string): STType => {
        const t: STType = {id: crypto.randomUUID(), name, color, showInFilter, iconName}
        saveTypes([...getTypes(), t])
        return t
    }

    export const updateType = (id: string, patch: Partial<STType>): void => {
        saveTypes(getTypes().map(t => t.id === id ? {...t, ...patch} : t))
    }

    export const deleteType = async (id: string): Promise<void> => {
        saveTypes(getTypes().filter(t => t.id !== id))
        try { await reqWrite<{deleted: string}>(`/types/${id}`, {method: "DELETE"}) }
        catch (e) { console.warn("[STApi] deleteType API call failed:", e) }
    }

    // ── API upsert — push type/category to the server ─────────

    export const getUsageCounts = (): Promise<{types: Record<string, number>, categories: Record<string, number>}> =>
        req<{types: Record<string, number>, categories: Record<string, number>}>("/library/usage-counts")

    export const upsertType = (type: STType): Promise<STType> =>
        reqWrite<STType>(`/types/${type.id}`, {method: "PUT", body: JSON.stringify(type)})

    export const upsertCategory = (cat: STCategory): Promise<STCategory> =>
        reqWrite<STCategory>(`/categories/${cat.id}`, {method: "PUT", body: JSON.stringify(cat)})
}
